import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, Clock, RotateCcw, XCircle } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidatesList,
  useEffectiveUser,
  useEnrollmentsForCandidate,
  useExpedienteForCandidateStandard,
  useLmsProgress,
  useLmsRoute,
} from "@/store/hooks";

export default function CandidateLesson() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("alumno"), [setRole]);
  const submitLmsQuiz = useDemoStore((s) => s.submitLmsQuiz);

  const [, params] = useRoute("/alumno/ruta/modulo/:moduloId");
  const moduloId = params?.moduloId;
  const [, navigate] = useLocation();

  const effective = useEffectiveUser();
  const candList = useCandidatesList();
  const candidateId = effective && effective.roles.includes("alumno") ? effective.id : candList[0]?.id;
  const enrollments = useEnrollmentsForCandidate(candidateId);
  const codigo = enrollments.find((c) => c === "EC1781") ?? "EC1781";
  const exp = useExpedienteForCandidateStandard(candidateId, codigo);
  const route = useLmsRoute(codigo);
  const progress = useLmsProgress(exp?.id);

  const rawMod = useMemo(() => route?.modules.find((m) => m.id === moduloId), [route, moduloId]);
  useEffect(() => {
    if (rawMod && rawMod.kind === "simulador-preop") {
      navigate(`/alumno/ruta/simulador/${rawMod.id}`, { replace: true });
    }
  }, [rawMod, navigate]);
  const mod = rawMod && rawMod.kind !== "simulador-preop" ? rawMod : undefined;
  const nextMod = useMemo(() => {
    if (!route || !mod) return undefined;
    const idx = route.modules.findIndex((m) => m.id === mod.id);
    return route.modules[idx + 1];
  }, [route, mod]);

  const lastAttempt = mod ? (progress.attemptsByModule[mod.id] ?? []).at(-1) : undefined;
  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    mod ? Array(mod.quiz.length).fill(null) : []
  );
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<{ aciertos: number; total: number; aprobado: boolean } | null>(null);

  useEffect(() => {
    if (!mod) return;
    setAnswers(Array(mod.quiz.length).fill(null));
    setSubmitted(false);
    setResult(null);
  }, [mod?.id]);

  if (!exp || !route || !mod) {
    return (
      <AppLayout pageTitle="Módulo">
        <Card>
          <div style={{ padding: 24, textAlign: "center", color: "var(--cc-gray-600)" }}>
            Módulo no encontrado. <Link href="/alumno/ruta">Volver a la ruta</Link>.
          </div>
        </Card>
      </AppLayout>
    );
  }

  const onSubmit = () => {
    const respuestas = answers.map((a) => (a == null ? -1 : a));
    const r = submitLmsQuiz(exp.id, mod.id, respuestas, candidateId ?? undefined);
    setResult({ aciertos: r.aciertos, total: r.total, aprobado: r.aprobado });
    setSubmitted(true);
  };
  const onRetry = () => {
    setAnswers(Array(mod.quiz.length).fill(null));
    setSubmitted(false);
    setResult(null);
  };

  const allAnswered = answers.every((a) => a != null);

  return (
    <AppLayout pageTitle={mod.titulo}>
      <div style={{ marginBottom: 14 }}>
        <Link href="/alumno/ruta" data-testid="link-back-ruta">
          <PrimaryButton variant="ghost" size="sm"><ArrowLeft size={12} /> Volver a la ruta</PrimaryButton>
        </Link>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 320px", gap: 16, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card testId="lesson-content">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <BookOpen size={16} color="var(--cc-teal)" />
              <Badge tone="teal">Lección</Badge>
              <Badge tone="gray"><Clock size={10} /> {mod.duracionMin} min</Badge>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 8 }}>{mod.titulo}</h2>
            <p style={{ fontSize: 13, color: "var(--cc-gray-600)", marginBottom: 12 }}>{mod.descripcion}</p>

            {mod.videoUrl && (
              <div style={{ aspectRatio: "16 / 9", background: "var(--cc-gray-900, #0f172a)", borderRadius: 8, overflow: "hidden", marginBottom: 14, position: "relative" }}>
                {/* Static poster (no autoplay) — keeps demo offline-friendly */}
                <div
                  style={{
                    position: "absolute", inset: 0,
                    background: "linear-gradient(135deg, var(--cc-navy), var(--cc-teal))",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "white", flexDirection: "column", gap: 8,
                  }}
                >
                  <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <BookOpen size={28} />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Video formativo (simulado)</div>
                  <div style={{ fontSize: 11, opacity: 0.7 }}>{mod.videoUrl}</div>
                </div>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {mod.contenido.map((p, i) => (
                <p key={i} style={{ fontSize: 13, color: "var(--cc-gray-700)", lineHeight: 1.6 }}>{p}</p>
              ))}
            </div>
          </Card>

          <Card testId="lesson-quiz">
            <SectionTitle>Quiz — 3 preguntas (necesitas 2 aciertos)</SectionTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {mod.quiz.map((q, qi) => {
                const ans = answers[qi];
                const reveal = submitted;
                return (
                  <div key={q.id} style={{ padding: 12, border: "1px solid var(--cc-gray-100)", borderRadius: 8 }} data-testid={`quiz-q-${qi}`}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)", marginBottom: 8 }}>
                      {qi + 1}. {q.enunciado}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {q.opciones.map((opt, oi) => {
                        const selected = ans === oi;
                        const isCorrect = reveal && oi === q.correcta;
                        const isWrong = reveal && selected && oi !== q.correcta;
                        return (
                          <label
                            key={oi}
                            data-testid={`quiz-q-${qi}-opt-${oi}`}
                            style={{
                              display: "flex", alignItems: "center", gap: 8,
                              padding: "7px 10px", borderRadius: 6, fontSize: 12,
                              cursor: submitted ? "default" : "pointer",
                              background: isCorrect ? "var(--cc-green-bg)" : isWrong ? "var(--cc-red-bg)" : selected ? "var(--cc-teal-bg)" : "transparent",
                              border: `1px solid ${isCorrect ? "var(--cc-green)" : isWrong ? "var(--cc-red)" : selected ? "var(--cc-teal)" : "var(--cc-gray-200)"}`,
                              color: isCorrect ? "var(--cc-green)" : isWrong ? "var(--cc-red)" : "var(--cc-gray-700)",
                              fontWeight: selected || isCorrect ? 600 : 400,
                            }}
                          >
                            <input
                              type="radio"
                              name={`q-${qi}`}
                              disabled={submitted}
                              checked={selected}
                              onChange={() => {
                                if (submitted) return;
                                const copy = [...answers];
                                copy[qi] = oi;
                                setAnswers(copy);
                              }}
                            />
                            <span style={{ flex: 1 }}>{opt}</span>
                            {isCorrect && <CheckCircle2 size={14} />}
                            {isWrong && <XCircle size={14} />}
                          </label>
                        );
                      })}
                    </div>
                    {reveal && q.explicacion && (
                      <div style={{ marginTop: 8, padding: 8, background: "var(--cc-gray-50, #fafbfc)", borderRadius: 6, fontSize: 11, color: "var(--cc-gray-700)" }}>
                        💡 {q.explicacion}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {!submitted ? (
                <PrimaryButton onClick={onSubmit} disabled={!allAnswered} testId="button-submit-quiz">
                  Enviar respuestas <ArrowRight size={12} />
                </PrimaryButton>
              ) : (
                <>
                  <PrimaryButton variant="outline" onClick={onRetry} testId="button-retry-quiz">
                    <RotateCcw size={12} /> Reintentar
                  </PrimaryButton>
                  {result?.aprobado && nextMod && (
                    <PrimaryButton onClick={() => navigate(nextMod.kind === "simulador-preop" ? `/alumno/ruta/simulador/${nextMod.id}` : `/alumno/ruta/modulo/${nextMod.id}`)} testId="button-next-module">
                      Siguiente módulo <ArrowRight size={12} />
                    </PrimaryButton>
                  )}
                  {result?.aprobado && !nextMod && (
                    <Link href="/alumno/ruta" data-testid="link-to-ruta-finished">
                      <PrimaryButton>Volver a la ruta <ArrowRight size={12} /></PrimaryButton>
                    </Link>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card>
            <SectionTitle>Tu resultado</SectionTitle>
            {result ? (
              <div data-testid="quiz-result">
                <div style={{ fontSize: 32, fontWeight: 700, color: result.aprobado ? "var(--cc-green)" : "var(--cc-amber)" }}>
                  {result.aciertos}/{result.total}
                </div>
                <Badge tone={result.aprobado ? "green" : "amber"}>
                  {result.aprobado ? "✓ Módulo aprobado" : "Necesitas 2 aciertos"}
                </Badge>
                <div style={{ marginTop: 10, fontSize: 12, color: "var(--cc-gray-600)" }}>
                  {result.aprobado
                    ? "Se registró en tu expediente y la bitácora del proceso."
                    : "Revisa las explicaciones y vuelve a intentarlo."}
                </div>
              </div>
            ) : lastAttempt ? (
              <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>
                Último intento: {lastAttempt.aciertos}/{lastAttempt.total} —{" "}
                <Badge tone={lastAttempt.aprobado ? "green" : "amber"}>
                  {lastAttempt.aprobado ? "Aprobado" : "Pendiente"}
                </Badge>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>
                Aún no has presentado este quiz.
              </div>
            )}
          </Card>

          {mod.criteriosCubiertos && mod.criteriosCubiertos.length > 0 && (
            <Card>
              <SectionTitle>Criterios cubiertos</SectionTitle>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {mod.criteriosCubiertos.map((c) => (
                  <Badge key={c} tone="navy">{c}</Badge>
                ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: "var(--cc-gray-500)" }}>
                Estos criterios serán evaluados también en la práctica con tu evaluador.
              </div>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
